import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";
import { toast } from "sonner";

export default function AdminLogin() {
    const { user, login, loading } = useAdminAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-cream">
                <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
        );
    }
    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            nav("/admin", { replace: true });
        } catch (err) {
            const msg = err.response?.data?.detail || "Credenciais inválidas";
            setError(typeof msg === "string" ? msg : "Credenciais inválidas");
            toast.error(typeof msg === "string" ? msg : "Credenciais inválidas");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-brand-cream relative overflow-hidden">
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.05] bg-hex-pattern pointer-events-none"
                style={{ backgroundImage: "url('/brand/pattern-1.png')" }}
            />
            <div
                aria-hidden
                className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-brand-green/15 blur-3xl"
            />
            <div
                aria-hidden
                className="absolute -bottom-40 -left-40 h-[500px] w-[500px] rounded-full bg-brand-orange/15 blur-3xl"
            />

            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="relative w-full max-w-md bg-white rounded-2xl shadow-pop p-8 md:p-10"
            >
                <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-brand-green flex items-center justify-center">
                        <ShieldCheck className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-brand-orange">
                            Sabor Líquido
                        </div>
                        <h1 className="font-display font-medium text-2xl text-brand-ink">
                            Painel de Admin
                        </h1>
                    </div>
                </div>

                <p className="mt-6 text-sm text-brand-ink-soft">
                    Entra com as tuas credenciais para aceder aos leads, encomendas e estatísticas.
                </p>

                <div className="mt-8 space-y-4">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-brand-ink-soft mb-1.5 block">
                            Email
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
                            <input
                                type="email"
                                required
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="admin-login-email"
                                placeholder="admin@saborliquido.pt"
                                className="w-full rounded-2xl border-2 border-brand-green/15 bg-white pl-11 pr-4 py-3.5 text-sm text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-brand-ink-soft mb-1.5 block">
                            Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-ink-soft" />
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="admin-login-password"
                                placeholder="••••••••"
                                className="w-full rounded-2xl border-2 border-brand-green/15 bg-white pl-11 pr-4 py-3.5 text-sm text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green"
                            />
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="admin-login-submit"
                    className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-6 py-4 text-sm font-semibold transition-all shadow-pop disabled:opacity-60"
                >
                    {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Lock className="h-4 w-4" />
                    )}
                    {submitting ? "A entrar..." : "Entrar"}
                </button>

                {error && (
                    <div
                        role="alert"
                        data-testid="admin-login-error"
                        className="mt-4 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm font-semibold text-red-700 text-center"
                    >
                        {error}
                    </div>
                )}

                <p className="mt-6 text-center text-xs text-brand-ink-soft">
                    Acesso restrito · Apenas administradores
                </p>
            </form>
        </div>
    );
}
